import {
  attr,
  computedOf,
  Fragment,
  html,
  MapSignal,
  on,
  OnDispose,
  prop,
  type Prop,
} from '@tempots/dom'
import type { SavedDesign } from '@rh/shared/schemas/design'
import {
  createWizardState,
  type WizardPreferences,
  type WizardState,
  type WizardStep,
} from './wizard-state'
import { commitWriteBacks, hasWriteBacks } from './profile-writeback'
import { ChoosePathStep } from './steps/choose-path-step'
import { ShapeStep } from './steps/shape-step'
import { InfoStep } from './steps/info-step'
import { PreviewStep } from './steps/preview-step'
import { UploadStep } from './steps/upload-step'
import { MarkBandsStep } from './steps/mark-bands-step'
import { ReviewStep } from './steps/review-step'
import './wizard.css'

export interface WizardOverlayOptions {
  open: Prop<boolean>
  agencyId: string
  agencyName: string
  preferences: WizardPreferences
  /** True when the editor holds a real design (not just the stock seed). */
  replacesExistingDesign: boolean
  onComplete: (design: SavedDesign) => void
  onDismiss: () => void
}

interface StepMeta {
  title: string
  subtitle: string
}

const STEP_META: Record<WizardStep, StepMeta> = {
  choose: {
    title: 'Set up your header',
    subtitle: 'How would you like to get your branding onto every resume?',
  },
  upload: {
    title: 'Upload a branded document',
    subtitle:
      'A resume you’ve already sent out works best — anything with your header on it.',
  },
  bands: {
    title: 'Mark your header',
    subtitle:
      'Show us where your header ends, where your footer starts, and where your logo is.',
  },
  review: {
    title: 'Check what we found',
    subtitle:
      'Confirm whose details these are — we’ll save them and keep your headers in sync.',
  },
  shape: {
    title: 'Pick a layout',
    subtitle: 'Which arrangement is closest to what you want?',
  },
  info: {
    title: 'Your details',
    subtitle:
      'We save these once, and your headers stay in sync with them from then on.',
  },
  preview: {
    title: 'Here’s your design',
    subtitle:
      'This is exactly how resumes will look. Open it in the editor to tweak anything.',
  },
}

/** Steps in order for each path, for Back navigation. */
const PATH_STEPS: Record<'import' | 'build', WizardStep[]> = {
  import: ['choose', 'upload', 'bands', 'review', 'preview'],
  build: ['choose', 'shape', 'info', 'preview'],
}

export function WizardOverlay(options: WizardOverlayOptions) {
  const { open, agencyId, agencyName, preferences } = options

  return MapSignal(open, isOpen => {
    if (!isOpen) return html.span()
    // Fresh state per opening: reopening the wizard starts over rather than
    // resuming a half-finished run from earlier in the session.
    const state: WizardState = createWizardState(
      agencyId,
      agencyName,
      preferences
    )

    const goBack = () => {
      const steps = PATH_STEPS[state.path.value]
      const idx = steps.indexOf(state.step.value)
      if (idx > 0) state.step.set(steps[idx - 1])
    }

    const dismiss = () => {
      options.onDismiss()
      open.set(false)
    }

    // Finishing first persists any profile write-backs the steps promised
    // (see profile-writeback.ts), then hands the design to the editor. On
    // failure nothing is handed off — tokens pointing at unsaved values
    // would render blanks.
    const finishing = prop(false)
    const finishError = prop<string | null>(null)
    const finish = async () => {
      const design = state.result.value
      if (!design || finishing.value) return
      finishing.set(true)
      finishError.set(null)
      try {
        await commitWriteBacks(agencyId, state.writeBacks.value)
        options.onComplete(design)
        open.set(false)
      } catch (e) {
        console.error('wizard write-back failed', e)
        finishError.set(
          'We couldn’t save your details — check your connection and try again.'
        )
      } finally {
        finishing.set(false)
      }
    }

    // While the wizard is open, a file dropped anywhere outside the upload
    // step's drop zone must not trigger the browser default (navigating
    // the SPA away to the dropped PDF).
    const swallowDrag = (e: DragEvent) => e.preventDefault()
    window.addEventListener('dragover', swallowDrag)
    window.addEventListener('drop', swallowDrag)

    return html.div(
      attr.class('design-wizard-backdrop'),
      OnDispose(() => {
        window.removeEventListener('dragover', swallowDrag)
        window.removeEventListener('drop', swallowDrag)
      }),
      // Swallow editor-level click/mousedown handlers (deselection etc.).
      on.mousedown((e: MouseEvent) => e.stopPropagation()),
      on.click((e: MouseEvent) => e.stopPropagation()),
      html.div(
        attr.class('design-wizard'),
        MapSignal(state.step, step =>
          Fragment(
            html.div(
              attr.class('design-wizard-header'),
              html.div(
                html.div(
                  attr.class('design-wizard-title'),
                  STEP_META[step].title
                ),
                html.div(
                  attr.class('design-wizard-subtitle'),
                  STEP_META[step].subtitle
                )
              ),
              html.button(
                attr.type('button'),
                attr.class('design-wizard-close'),
                attr.title('Close and use the editor'),
                on.click(dismiss),
                '×'
              )
            ),
            html.div(
              attr.class('design-wizard-body'),
              renderStep(step, state, options, dismiss)
            ),
            html.div(
              attr.class('design-wizard-footer'),
              html.div(
                // The choose step's third tile ("build from scratch") covers
                // skipping, so no footer link there.
                step !== 'choose'
                  ? html.button(
                      attr.type('button'),
                      attr.class('wiz-btn wiz-btn-secondary'),
                      on.click(goBack),
                      '← Back'
                    )
                  : html.span()
              ),
              step === 'preview'
                ? html.div(
                    attr.class('design-wizard-footer-note'),
                    MapSignal(finishError, err =>
                      err
                        ? html.span(attr.class('wiz-error'), err)
                        : Fragment(
                            options.replacesExistingDesign
                              ? html.div(
                                  'This replaces your current design (Undo brings it back; nothing is saved until you press Save).'
                                )
                              : html.span(),
                            MapSignal(state.writeBacks, wb =>
                              hasWriteBacks(wb)
                                ? html.div(
                                    'Finishing also saves the details you entered to your profiles.'
                                  )
                                : html.span()
                            )
                          )
                    )
                  )
                : html.span(),
              // Steps render their own primary action into the body; the
              // footer's primary is only the preview hand-off.
              step === 'preview'
                ? html.button(
                    attr.type('button'),
                    attr.class('wiz-btn wiz-btn-primary'),
                    attr.disabled(
                      computedOf(
                        state.result,
                        finishing
                      )((r, busy) => r === null || busy)
                    ),
                    on.click(() => void finish()),
                    MapSignal(finishing, busy =>
                      html.span(
                        busy ? 'Saving your details…' : 'Open in editor'
                      )
                    )
                  )
                : html.span()
            )
          )
        )
      )
    )
  })
}

function renderStep(
  step: WizardStep,
  state: WizardState,
  options: WizardOverlayOptions,
  dismiss: () => void
) {
  switch (step) {
    case 'choose':
      return ChoosePathStep(state, { onSkipToEditor: dismiss })
    case 'upload':
      return UploadStep(state)
    case 'bands':
      return MarkBandsStep(state, options.agencyId)
    case 'review':
      return ReviewStep(state, options.agencyId)
    case 'shape':
      return ShapeStep(state, options.agencyId)
    case 'info':
      return InfoStep(state, options.agencyId)
    case 'preview':
      return PreviewStep(state, options.agencyId)
  }
}
